/**
 * `t(context, key, params?)` — the one way a shopper-facing string reaches the screen (feature 0050).
 *
 * ── Why this is a plain function and NOT a method on RenderContext ─────────────────────────────────────
 *
 * The first design put `t()` on the render context. It was rejected on its failure mode, and the reason is
 * worth keeping written down because it is not obvious:
 *
 *  - **85 test files** build the context with `as unknown as RenderContext`. The double cast switches the
 *    typecheck off, so a context missing `t` compiles clean and nothing warns.
 *  - **Three production contexts** are rebuilt by hand in the browser (`buybox-island`, `mainproduct-island`,
 *    `collection-island`) — they are assembled from a JSON payload, not from the server's object.
 *  - **No error boundary** wraps an island root (`hydrateRoot`, bare). A missing `context.t` throws AT THE
 *    ACCESS, upstream of any internal try/catch, and React 18 unmounts the whole root: the buy box vanishes
 *    from the DOM with no error visible anywhere. That is the "inert island, zero errors" failure this
 *    codebase has already lived through twice.
 *
 * So: a total function over an UNKNOWN context. It reads what it can, falls back when it cannot, and
 * returns a string in every case — including when handed `null`, a number, or an object from a fixture
 * written two years before this file existed. **An incomplete context degrades; it never unmounts a shop.**
 *
 * ── The order of resolution ────────────────────────────────────────────────────────────────────────────
 *
 *   1. the dictionary carried BY the context — this is the one with the seller's own overrides merged in
 *      server-side, so it has to win
 *   2. the bundled dictionary for `context.locale`
 *   3. the bundled French dictionary — the language every one of our sentences is authored in
 *   4. the key itself
 *
 * Step 4 renders `cart.empty_title` on the page, which looks broken — deliberately. A missing key is a bug
 * in our dictionary, and a visible one gets fixed; a silent empty string is a blank button nobody reports.
 */
import { isPlural, } from './dictionary';
import { bundledDictionary, fallbackDictionary } from './bundled-registry';
import { FALLBACK_LOCALE } from './fallback';
/** Read the locale off a context of unknown shape. */
export function localeOf(context) {
    const raw = context?.locale;
    return typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : FALLBACK_LOCALE;
}
/** Read the context-carried dictionary, if there is one and it looks like a dictionary. */
function textsOf(context) {
    const raw = context?.texts;
    return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
        ? raw
        : undefined;
}
/**
 * Walk the sources in order and return the first that has this key.
 *
 * The middle step reads through {@link ./bundled-registry} rather than the dictionary barrel. Same answer
 * on the server, where the registry holds all twenty-six; in the BROWSER it holds only French, because a
 * foreign-language page carries its own sentences in `context.texts` — the first step here — and shipping
 * the other twenty-five to read one of them cost 211 KB per visitor. French stays the floor either way.
 */
function lookup(context, key) {
    const locale = localeOf(context);
    const carried = textsOf(context);
    return carried?.[key] ?? bundledDictionary(locale)?.[key] ?? fallbackDictionary()[key];
}
/**
 * Pick the plural form for [count] in [locale].
 *
 * `Intl.PluralRules` is wrapped because it THROWS on a malformed locale tag, and locales reach us from a
 * database column and from a JSON payload in the browser — neither is guaranteed to be a valid BCP-47 tag.
 * A shop must not lose its cart page because someone typed `fr_FR` into a settings field.
 */
function pluralForm(forms, count, locale) {
    let category = 'other';
    try {
        category = new Intl.PluralRules(locale).select(count);
    }
    catch {
        category = count === 1 ? 'one' : 'other';
    }
    // `zero` is not a CLDR category in every language, but it is the form a shop wants for an empty cart, so
    // an explicit `zero` wins for exactly 0 wherever the author wrote one.
    //
    // CAUTION, and Latvian is why: this slot does two jobs. Where `zero` IS a CLDR category it also fires on
    // numbers that are not nought — Latvian counts 0, 10-19 and every round ten as `zero` — and the switch
    // below returns the same string for all of them. So in such a language `zero` must hold the COUNTED
    // sentence, never a bespoke "your cart is empty" one, or a buyer who lost eleven offers is told nothing
    // went wrong. `lv.ts` says so at the key where it bites.
    if (count === 0 && typeof forms.zero === 'string')
        return forms.zero;
    // Written out rather than indexed: an interface has no implicit index signature, and reaching for a cast
    // to get one would also swallow a typo in a CLDR category name. `other` is the floor every language has.
    switch (category) {
        case 'zero':
            return forms.zero ?? forms.other;
        case 'one':
            return forms.one ?? forms.other;
        case 'two':
            return forms.two ?? forms.other;
        case 'few':
            return forms.few ?? forms.other;
        case 'many':
            return forms.many ?? forms.other;
        default:
            return forms.other;
    }
}
/**
 * Replace `{name}` placeholders. An unknown placeholder is left AS IT IS rather than blanked: seeing
 * `Bonjour {name}` on the page tells you exactly which parameter the caller forgot, where an empty gap
 * tells you nothing.
 */
function interpolate(template, params) {
    if (!params)
        return template;
    return template.replace(/\{(\w+)\}/g, (whole, name) => {
        const value = params[name];
        return value === undefined || value === null ? whole : String(value);
    });
}
/** The reader's own digits and grouping for a counted quantity. */
function formatCardinal(value, locale) {
    try {
        return new Intl.NumberFormat(locale).format(value);
    }
    catch {
        return String(value);
    }
}
/**
 * Translate [key] for [context].
 *
 * @param context anything carrying `locale` and optionally `texts` — a RenderContext, an island payload, or
 *                nothing at all. Never trusted, never required.
 * @param key     a stable dotted key, e.g. `cart.empty_title`. Its prefix is the category the seller sees.
 * @param params  named values for `{placeholders}`; a `count` also selects the plural form.
 */
export function t(context, key, params) {
    const phrase = lookup(context, key);
    if (isPlural(phrase)) {
        const locale = localeOf(context);
        const count = typeof params?.['count'] === 'number' ? params['count'] : 0;
        // A PLURAL phrase's `count` is a cardinal quantity by definition — it is what chose the form — so it is
        // printed the way the reader writes quantities: `1,535 reviews` in English, `1 535 avis` in French,
        // `١٥٣٥` in Arabic. Only here, and only for `count`: a plain phrase's `{year}` or `{id}` must stay the
        // digits it was given, and `2,026` would be a bug.
        return interpolate(pluralForm(phrase, count, locale), {
            ...params,
            count: formatCardinal(count, locale),
        });
    }
    // Absent, or present but not a sentence — a hand-edited dictionary, a seller override stored as a number.
    // Both are bugs in the dictionary and both render the key, which is the form of failure that gets noticed.
    if (typeof phrase !== 'string')
        return key;
    return interpolate(phrase, params);
}
/**
 * The same translation, bound to a dictionary and a locale rather than to a context — for the imperative
 * engines (`videoInit`, `sliderInit`, `trackOrderInit`) and the islands, which mutate the DOM and never see
 * a render context at all. They read the page's `aqx-i18n` block and bind once.
 */
export function boundTranslator(locale, texts) {
    const context = { locale, texts };
    return (key, params) => t(context, key, params);
}
//# sourceMappingURL=translate.js.map